import type { Money, OrderDetail } from '@/lib/api-admin/types';
import type { OrderStatement, TransactionStatement } from './types';
import { formatOrderItemOption } from '../lib/orderItemOption';
import { localeFromCurrency, type DocumentLocaleCode } from './printLabels';

/**
 * 계약 도착 전/미배포 폴백 — 기존 OrderDetail 에서 2종 출력물(거래명세서·주문내역서)의
 * 정규화 모델을 파생한다. (견적서는 BO 범위 밖 — SF 장바구니에서 구현.)
 *
 * be-order print DTO 엔드포인트가 응답하면 hook 이 BE 데이터로 대체한다(정규화 모델 동일 형태).
 * 이 파생기는 BO 가 데이터 출처와 무관하게 레이아웃을 먼저 완성/검증하도록 한다.
 * OrderDetail 에 없는 공급사명/상품코드/할인분리 등은 안전한 폴백으로 채운다.
 */

/** Phase1 단일 공급사(§5, Lead 확정). */
const DEFAULT_SUPPLIER_NAME = 'EVERLINE';

/**
 * [233] 해외(글로벌) 배송지 — be-order ShippingAddressDto 의 확장 필드. generated 타입
 * (OrderShipmentInfo.shippingAddress)에 반영되기 전까지 read 전용으로 안전 캐스팅한다.
 * OrderDetailPage 의 동명 타입과 동일 구조(JsonInclude NON_NULL → 국내 주문은 미전송).
 */
interface GlobalShippingAddress {
  zipCode?: string | null;
  address1?: string | null;
  address2?: string | null;
  receiverName?: string | null;
  receiverMobile?: string | null;
  recipientEnglishName?: string | null;
  recipientMobile?: string | null;
  city?: string | null;
  stateProvince?: string | null;
  countryCode?: string | null;
  remarks?: string | null;
}

const zeroMoney = (currencyCode: string): Money => ({ amount: 0, currencyCode });

const addMoney = (a: Money, b: Money): Money => ({
  amount: a.amount + b.amount,
  currencyCode: a.currencyCode || b.currencyCode,
});

/** 라인 합계 — 통화는 첫 라인 기준(혼합 통화 미가정). */
const sumLines = (
  lines: ReadonlyArray<{ itemTotal: Money }>,
  fallbackCurrency: string,
): Money =>
  lines.reduce((acc, l) => addMoney(acc, l.itemTotal), zeroMoney(fallbackCurrency));

const currencyOf = (order: OrderDetail): string =>
  order.paymentAmount?.currencyCode ??
  order.items[0]?.unitPrice.currencyCode ??
  'KRW';

/**
 * [글로벌몰] 폴백 locale 도출 — OrderDetail 에는 order_mall/localeCode 가 없으므로
 * 결제통화로 best-effort 추정(JPY→ja-JP, USD→en-US, CNY→zh-CN, KRW/그외→ko-KR).
 * be-order print DTO 엔드포인트가 응답하면 DTO 의 localeCode 가 이 폴백을 대체한다.
 * 한국몰(KRW) 주문은 항상 ko-KR 로 떨어져 종전과 동일(회귀 0).
 */
const localeOf = (order: OrderDetail): DocumentLocaleCode =>
  localeFromCurrency(currencyOf(order)) ?? 'ko-KR';

/**
 * OrderLineItem 의 상품코드 — 코드 칸은 자체상품코드(productCode)만 노출한다.
 * OrderDetail(generated)에는 아직 productCode 가 없으므로 부재 시 빈 문자열로 둔다.
 * 내부 UUID(productId)는 화면에 노출하지 않는다(항목4 규칙 · be-order legacy "" 정책과 정합).
 * 문서 컴포넌트가 빈값을 '-' 로 표기한다.
 */
const productCodeOf = (item: OrderDetail['items'][number]): string =>
  (item as { productCode?: string | null }).productCode ?? '';

const supplierNameOf = (item: OrderDetail['items'][number]): string =>
  (item as { supplierName?: string | null }).supplierName ?? DEFAULT_SUPPLIER_NAME;

export const deriveTransactionStatement = (order: OrderDetail): TransactionStatement => {
  const currency = currencyOf(order);
  const items = order.items.map((item) => ({
    productCode: productCodeOf(item),
    productName: item.productName,
    // QA-R6 I: variantLabel 단일 폴백 → 구조화 selectedOptions 우선 표기(조합/추가옵션이 '-' 로 비던 결함).
    optionLabel: formatOrderItemOption(item),
    quantity: item.quantity,
    unitPrice: item.unitPrice,
    itemTotal: item.lineAmount,
  }));
  const itemsSubtotal = sumLines(items, currency);
  // OrderDetail 은 배송비/할인 분리 필드를 갖지 않으므로 폴백 0(§5 규칙은 BE 가 수행).
  const discountAmount = zeroMoney(currency);
  const shippingFee = zeroMoney(currency);
  // 폴백 합계 = 결제금액(있으면) 우선, 없으면 itemsSubtotal.
  const total = order.paymentAmount ?? itemsSubtotal;
  return {
    documentType: 'TRANSACTION_STATEMENT',
    orderId: order.orderId,
    orderNo: order.orderNo,
    orderedAt: order.orderedAt ?? null,
    ordererName: order.ordererName ?? null,
    // [글로벌몰] 폴백 locale/통화 — 결제통화로 도출(한국몰=ko-KR/KRW 종전 동일).
    localeCode: localeOf(order),
    currencyCode: currency,
    items,
    summary: { itemsSubtotal, discountAmount, shippingFee, total },
  };
};

export const deriveOrderStatement = (order: OrderDetail): OrderStatement => {
  const currency = currencyOf(order);
  const items = order.items.map((item) => ({
    productCode: productCodeOf(item),
    productName: item.productName,
    supplierName: supplierNameOf(item),
    imageUrl: item.thumbnailUrl ?? null,
    // QA-R6 I: variantLabel 단일 폴백 → 구조화 selectedOptions 우선 표기(조합/추가옵션 '-' 결함 대응).
    optionLabel: formatOrderItemOption(item),
    quantity: item.quantity,
    unitPrice: item.unitPrice,
    itemTotal: item.lineAmount,
  }));
  const itemsSubtotal = sumLines(items, currency);
  // [233] 해외(글로벌) 배송지 확장 필드 — generated OrderShipmentInfo.shippingAddress 에
  // 반영되기 전까지 read 전용으로 안전 캐스팅한다(OrderDetailPage GlobalShippingAddress 와 동일).
  // 국내 주문은 확장 필드가 undefined → 폴백 모델에서 자연 생략(회귀 0).
  const addr = (order.shipmentInfo?.shippingAddress ?? null) as GlobalShippingAddress | null;
  const recipient = addr
    ? {
        name: addr.receiverName ?? order.ordererName ?? null,
        phone: addr.receiverMobile ?? order.ordererMobile ?? null,
        postalCode: addr.zipCode ?? null,
        address1: addr.address1 ?? null,
        address2: addr.address2 ?? null,
        englishName: addr.recipientEnglishName ?? null,
        countryCode: addr.countryCode ?? null,
        city: addr.city ?? null,
        stateProvince: addr.stateProvince ?? null,
        mobile: addr.recipientMobile ?? null,
        remarks: addr.remarks ?? null,
      }
    : null;
  return {
    documentType: 'ORDER_STATEMENT',
    orderId: order.orderId,
    orderNo: order.orderNo,
    orderedAt: order.orderedAt ?? null,
    ordererName: order.ordererName ?? null,
    ordererPhone: order.ordererMobile ?? null,
    // [글로벌몰] 폴백 locale/통화 — 결제통화로 도출(한국몰=ko-KR/KRW 종전 동일).
    localeCode: localeOf(order),
    currencyCode: currency,
    recipient,
    items,
    payment: {
      method: order.paymentMethod ?? null,
      paidAt: order.paidAt ?? null,
      itemsSubtotal,
      shippingFee: zeroMoney(currency),
      discountAmount: zeroMoney(currency),
      mileageUsed: zeroMoney(currency),
      depositUsed: zeroMoney(currency),
      payableAmount: order.paymentAmount ?? itemsSubtotal,
    },
  };
};

